import { useEffect, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { AccountInfo } from '@azure/msal-browser';
import { TokenManager } from '../utils/tokenManager';
import { setMsalGlobalState } from '../utils/msalSetup';

interface AuthState {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  isLoading: boolean;
  error: string | null;
}

export const useAuth = () => {
  const { instance, accounts } = useMsal();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
    error: null,
  });

  const acquireTokens = useCallback(
    async (account: AccountInfo) => {
      try {
        const clientId = process.env.REACT_APP_CLIENT_ID;
        if (!clientId) {
          throw new Error('REACT_APP_CLIENT_ID environment variable is not set');
        }

        // Use the new initializeTokens method for consistency
        await TokenManager.initializeTokens(instance, account, clientId);

        // Fetch the user's country from Entra ID (Microsoft Graph) only the
        // first time the user accesses the application. Non-fatal: a failure
        // here should not block authentication.
        try {
          await TokenManager.fetchUserCountry(instance, account);
        } catch (countryError) {
          console.warn('[acquireTokens] Failed to fetch user country:', countryError);
        }

        if (!TokenManager.getRole() || TokenManager.getRole() === '') {
          TokenManager.setRole('Global Admin');
        }

        return true;
      } catch (error) {
        setAuthState((prev) => ({ ...prev, error: 'Failed to acquire tokens' }));
        return false;
      }
    },
    [instance]
  );

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Set MSAL global state for use by createAuthBaseQuery
        setMsalGlobalState(instance, accounts);
        
        if (accounts.length > 0) {
          const activeAccount = accounts[0];
          instance.setActiveAccount(activeAccount);

          const tokensAcquired = await acquireTokens(activeAccount);

          setAuthState({
            isAuthenticated: tokensAcquired,
            user: activeAccount,
            isLoading: false,
            error: tokensAcquired ? null : 'Authentication failed',
          });
        } else {
          setAuthState({
            isAuthenticated: false,
            user: null,
            isLoading: false,
            error: null,
          });
        }
      } catch (error) {
        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
          error: 'Authentication initialization failed',
        });
      }
    };

    initializeAuth();
  }, [accounts, instance, acquireTokens]);

  return authState;
};
